import { useCallback } from 'react';

import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { ClaimFunnelEvents } from '@/feature/analytics/sbk/claim-funnel-events';
import { GASbkEvents } from '@/feature/analytics/sbk/ga-sbk-events';
import { useBetRejectedAlert } from '@/feature/betslip-sbk/components/betslip-action-tray/hooks/use-bet-rejected-alert';
import { useBetUnconfirmedAlert } from '@/feature/betslip-sbk/components/betslip-action-tray/hooks/use-bet-unconfirmed-alert';
import { usePrefetchBets } from '@/feature/betslip-sbk/components/betslip-action-tray/hooks/use-prefetch-bets';
import { betStakeLimitKeys } from '@/feature/betslip-sbk/hooks/use-bet-stake-limit';
import { SBK_BETSLIP_LOG_TAG, getActiveSbkBetSlipStore } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { BetSubmissionOutcome } from '@/feature/betslip-sbk/types';
import { getShareConversions } from '@/feature/betslip-sbk/utils/share-referrer';
import { markLinkConversion } from '@/feature/entry-share/utils/social-link';
import { userLimitsKeys } from '@/feature/responsible-gaming/hooks/query-keys';
import { getActiveClaim, useClaimFunnelStore } from '@/hooks/use-claim-funnel-store';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { BetSlipStackParamList } from '@/navigation/types';
import { logger } from '@/utils/logging';
import { useQueryClient } from '@tanstack/react-query';

const markSharedBetConversions = () => {
    const { referrer, submittedState, actions } = getActiveSbkBetSlipStore().getState();
    // a gold coin wager isn't attributed; the referrer stays for a later cash placement
    if (!referrer || submittedState.isCoinsWager) {
        return;
    }

    const conversions = getShareConversions(submittedState, referrer.optionIds);
    // a placement holding none of the shared legs keeps the referrer for a later one that does
    if (!conversions.length) {
        return;
    }

    conversions.forEach(conversion => markLinkConversion({ referrer, ...conversion }));
    actions.clearReferrer();
};

// Callback to handle logic when a bet submission is complete
export const useBetSubmissionComplete = () => {
    const { showBetRejectedAlert } = useBetRejectedAlert();
    const { showBetUnconfirmedAlert } = useBetUnconfirmedAlert();
    const navigation = useNavigation<NativeStackNavigationProp<BetSlipStackParamList>>();
    const queryClient = useQueryClient();
    const prefetchBets = usePrefetchBets();
    const isPredictions = useIsPredictionsProduct();

    return useCallback(
        (outcome: BetSubmissionOutcome) => {
            if (outcome.status === 'confirmed') {
                const { summary } = outcome;
                // Success bet submission
                logger.info(SBK_BETSLIP_LOG_TAG, 'Bet submission success', summary);
                // A freshly placed bet lands on the Upcoming tab (event not yet started). A prediction order
                // sits under Unmatched until the exchange confirms the fill, then moves to Upcoming.
                prefetchBets('Upcoming');
                if (isPredictions) {
                    prefetchBets('Unmatched');
                }

                const totalBets = summary.totalCount;
                GASbkEvents.betSlipInteractionSummary(totalBets, totalBets);
                GASbkEvents.viewBetConfirmation();

                // First Sweeps bet after a credited cross-sell claim - infers the $1 was used.
                if (getActiveClaim()?.creditAwaitingUse) {
                    ClaimFunnelEvents.creditUsed();
                    useClaimFunnelStore.getState().actions.setCreditAwaitingUse(false);
                }

                // userLimit needs to be updated after bet submission
                queryClient.invalidateQueries({ queryKey: userLimitsKeys.limits() });
                queryClient.invalidateQueries({ queryKey: betStakeLimitKeys.all });

                markSharedBetConversions();

                navigation.navigate('BetSuccessScreen');
            } else if (outcome.status === 'rejected') {
                showBetRejectedAlert();
            } else {
                showBetUnconfirmedAlert();
            }
        },
        [isPredictions, navigation, prefetchBets, queryClient, showBetRejectedAlert, showBetUnconfirmedAlert]
    );
};
